import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cooperplaceApi } from 'services/api';
import ApplicationLayout from 'v3/layouts/Application';
import { useSnackbar } from 'v3/components/Snackbar';
import { Text } from 'v3/components';
import { ChecklistData } from '../shared/Data';
import { useChecklist } from '../shared/context';
import { CheckListSchema } from '../components/RegisterComponents/validator';

export function Register() {
  const checklist = useChecklist();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [loadingSave, setLoadingSave] = useState(false);

  function isTrainingChecklistType(checklistType) {
    const slug = String(checklistType?.slug || '').toLowerCase();
    const name = String(checklistType?.name || checklistType?.label || '')
      .trim()
      .toLowerCase();

    return slug === 'training' || name === 'treinamento';
  }

  function validateTrainingChecklist(data, questions) {
    const errorList = {};
    const workload = String(data?.courseWorkload || '').trim();

    if (!String(data?.name || '').trim()) {
      errorList.name = 'Nome obrigatório';
    }

    if (!data?.checklistType?.id) {
      errorList.type = 'Tipo obrigatório';
    }

    if (!String(data?.description || '').trim()) {
      errorList.description = 'Descrição obrigatória';
    }

    if (!workload) {
      errorList.courseWorkload = 'Duração do treinamento obrigatória';
    } else if (!/^\d{2}:[0-5]\d$/.test(workload) || workload === '00:00') {
      errorList.courseWorkload = 'Informe a duração no formato HH:mm';
    }

    if (!Array.isArray(questions) || questions.length === 0) {
      errorList.questions = 'Inclua ao menos uma pergunta';
    }

    checklist.setErrors(errorList);

    return Object.keys(errorList).length === 0;
  }

  async function validate(data, setErrors) {
    const errorList = {};
    const dataObject = {
      name: data?.checklistInfo?.name,
      description: data?.checklistInfo?.description,
      type: data?.checklistInfo?.checklistType?.id,
      questions: data?.questions?.map(item => {
        return {
          checklist_id: item?.id,
          order: item?.order,
          required: item?.required,
        };
      }),
      clients: data?.checklistInfo?.clients,
      negotiation: data?.checklistInfo?.negotiation,
      vehicleType: data?.checklistInfo?.vehicleType,
      implementType: data?.checklistInfo?.implementType,
      vehicleBodyType: data?.checklistInfo?.vehicleBodyType,
      branchOffices: data?.checklistInfo?.branchOffices,
      hasDollyImplement: data?.checklistInfo?.hasDollyImplement,
      availableLogisticPanel: data?.checklistInfo?.availableLogisticPanel,
      defaultCostCenter: data?.checklistInfo?.defaultCostCenter,
      costCentersId: data?.checklistInfo?.costCentersId,
    };
    try {
      await CheckListSchema.validate(dataObject, {
        abortEarly: false,
      });
      setErrors({ ...errorList });

      return true;
    } catch (err) {
      err.inner.forEach(error => {
        errorList[error.path] = error.message;
      });
      setErrors({ ...errorList });

      return false;
    }
  }

  async function handleRegisterChecklist(questions) {
    if (loadingSave) return;

    try {
      setLoadingSave(true);

      const CHECKLIST_TYPE_SINGLE_SLUG = 'SeparateChecklist';
      const isSingleChecklist =
        (checklist?.data?.checklistType?.slug || '') === CHECKLIST_TYPE_SINGLE_SLUG;
      const isTrainingChecklist = isTrainingChecklistType(
        checklist?.data?.checklistType
      );

      if (isTrainingChecklist) {
        const isTrainingDataValid = validateTrainingChecklist(
          checklist?.data,
          questions
        );

        if (!isTrainingDataValid) {
          snackbar.show(<Text>Revise os campos obrigatórios.</Text>, {
            type: 'error',
            showClose: true,
          });
          setLoadingSave(false);
          return;
        }
      }

      if (isSingleChecklist) {
        const errorList = {};

        if (!checklist?.data?.name?.trim()) errorList.name = 'Título é obrigatório';
        if (!checklist?.data?.description?.trim()) errorList.description = 'Descrição é obrigatória';
        if (!checklist?.data?.checklistType?.id) errorList.type = 'Tipo de checklist é obrigatório';
        if (!Array.isArray(questions) || questions.length === 0) {
          errorList.questions = 'Inclua ao menos uma pergunta';
        }

        checklist.setErrors(errorList);

        if (Object.keys(errorList).length > 0) {
          snackbar.show(<Text>Revise os campos obrigatórios do checklist avulso.</Text>, {
            type: 'error',
            showClose: true,
          });
          setLoadingSave(false);
          return;
        }
      } else if (!isTrainingChecklist) {
        // validação original (checklist comum)
        const hasDollyImplement = !!checklist?.data?.implementType?.find(
          ({ name }) => name === 'Dolly'
        );
        const dataToValidate = { ...checklist?.data, hasDollyImplement };

        const isDataValid = await validate(
          { checklistInfo: dataToValidate, questions },
          checklist.setErrors
        );

        if (!isDataValid) {
          snackbar.show(<Text>Revise os campos obrigatórios.</Text>, {
            type: 'error',
            showClose: true,
          });
          setLoadingSave(false);
          return;
        }
      }

      if (isSingleChecklist) {
        const payload = {
          appVisible: !!checklist?.data?.appVisible,
          expirationDays: Number(checklist?.data?.singleDays || 0),
          active: !!checklist?.data?.active,
          name: checklist?.data?.name,
          description: checklist?.data?.description,
          checkTypeId: checklist?.data?.checklistType?.id,
          groupId: (checklist?.data?.groups || []).map(g => g.id),

          questions: (questions || []).map(q => ({
            questionId: q?.id,
            order: q?.order,
            required: q?.required === true || q?.required === 'true',
          })),
        };

        const { data } = await cooperplaceApi.post('checklists', payload);

        snackbar.show(<Text>Checklist avulso cadastrado com sucesso!</Text>, {
          type: 'success',
        });

        setLoadingSave(false);
        navigate(`/checklist/${data?.id}`);
        return;
      }

      const payload = isTrainingChecklist
        ? {
            require: false,
            default: true,
            active: !!checklist?.data?.active,
            name: checklist?.data?.name,
            description: checklist?.data?.description,
            courseWorkload: checklist?.data?.courseWorkload,
            checkTypeId: checklist?.data?.checklistType?.id,
            cnpjClientsId: [],
            negotiationId: null,
            vehiclesTypesId: [],
            vehiclesBodyId: [],
            vehiclesImplementsId: [],
            questions: (questions || []).map(q => ({
              questionId: q?.id,
              required: true,
              order: q?.order,
            })),
            availableForDriver: false,
            branchOffices: [],
            requiredInspectSign: false,
            requiredDriverSign: false,
            availableLogisticPanel: false,
            defaultCostCenter: false,
            serviceLevels: [],
            costCentersId: [],
          }
        : (() => {
            const formattedServiceLevels = (checklist.data.serviceLevels || []).map(sl => {
              const raw = sl.serviceLevel ?? sl.slug;
              const backendSlug = raw === 'BRONZE' ? 'AGREGADO' : raw;

              return {
                serviceLevel: backendSlug,
                days: sl.days ?? sl.checklistDays ?? 0,
                quantity: sl.quantity ?? sl.travelQuantity ?? 0,
                condition: sl.condition ?? (sl.orAnd ? 'OR' : 'AND'),
              };
            });

            return {
              require: checklist?.data?.required || false,
              default: checklist?.data?.default || false,
              active: checklist?.data?.active || false,
              name: checklist?.data?.name,
              description: checklist?.data?.description,
              checkTypeId: checklist?.data?.checklistType?.id,
              cnpjClientsId: checklist?.data?.clients?.map(item => item?.id),
              negotiationId: checklist?.data?.negotiation?.id,
              vehiclesTypesId: checklist?.data?.vehicleType?.map(item => item?.id),
              vehiclesBodyId: checklist?.data?.vehicleBodyType?.map(item => item?.id),
              vehiclesImplementsId: checklist?.data?.implementType?.map(item => item?.id),
              questions: (questions || []).map(q => ({
                questionId: q?.id,
                required: q?.required === true || q?.required === 'true',
                order: q?.order,
              })),
              availableForDriver: !!checklist?.data?.availableForDriver,
              branchOffices: (checklist.data.branchOffices || []).map(item => item?.id),
              requiredInspectSign: !!checklist?.data?.requiredInspectSign,
              requiredDriverSign: !!checklist?.data?.requiredDriverSign,
              availableLogisticPanel: !!checklist?.data?.availableLogisticPanel,
              defaultCostCenter: !!checklist.data.defaultCostCenter,
              serviceLevels: formattedServiceLevels,
              costCentersId: checklist.data.costCentersId || [],
            };
          })();

      const { data } = await cooperplaceApi.post('checklists', payload);

      snackbar.show(<Text>Checklist cadastrado com sucesso!</Text>, {
        type: 'success',
      });

      setLoadingSave(false);
      navigate(`/checklist/${data?.id}`);
    } catch (error) {
      const message =
        error?.response?.data?.error || 'Não foi possível cadastrar o checklist';

      setLoadingSave(false);

      snackbar.show(<Text>{message}</Text>, {
        type: 'error',
        showClose: true,
      });
    }
  }


  function handleCancel() {
    navigate('/checklist');
  }

  return (
    <ApplicationLayout title="Cadastro checklist">
      <ChecklistData
        handleSave={handleRegisterChecklist}
        isLoading={loadingSave}
        handleCancel={handleCancel}
      />
    </ApplicationLayout>
  );
}
